// Howie Knowledge Graph — Relationship list with type filter pills.
// Modeled on Alexandria's RelationshipList.

import React, { useState, useMemo } from 'react';
import { useHowieAnalytics } from '@/hooks/useHowieAnalytics';
import type { RelatedConcept } from './types';
import { RELATIONSHIP_COLORS, RELATIONSHIP_LABELS } from './constants';

interface RelationshipListProps {
  relatedConcepts: RelatedConcept[];
  onConceptClick: (canonicalId: string) => void;
}

const ALL_TYPES = ['RESONATES_WITH', 'CONTRADICTS', 'ENABLES', 'DEVELOPS'] as const;

export const RelationshipList: React.FC<RelationshipListProps> = ({
  relatedConcepts,
  onConceptClick,
}) => {
  const { track } = useHowieAnalytics();
  const [activeType, setActiveType] = useState<string | null>(null);

  const handleFilterChange = (nextType: string | null) => {
    setActiveType(nextType);
    // entity_type carries the relationship type the user filtered to ('all'
    // when clearing). No free-text / PII.
    track('graph_filter_changed', { entity_type: nextType ?? 'all' });
  };

  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const rc of relatedConcepts) {
      counts.set(rc.type, (counts.get(rc.type) ?? 0) + 1);
    }
    return counts;
  }, [relatedConcepts]);

  const filtered = useMemo(() => {
    if (!activeType) return relatedConcepts;
    return relatedConcepts.filter((rc) => rc.type === activeType);
  }, [relatedConcepts, activeType]);

  if (relatedConcepts.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {/* Type filter pills */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => handleFilterChange(null)}
          className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
          style={{
            background: activeType === null ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
            color: activeType === null ? '#f0ebe3' : '#a8b4c4',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          All ({relatedConcepts.length})
        </button>
        {ALL_TYPES.map((type) => {
          const count = typeCounts.get(type) ?? 0;
          if (count === 0) return null;
          const color = RELATIONSHIP_COLORS[type] ?? '#888';
          const isActive = activeType === type;
          return (
            <button
              key={type}
              onClick={() => handleFilterChange(isActive ? null : type)}
              className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
              style={{
                background: isActive ? `${color}30` : 'rgba(255,255,255,0.05)',
                color: isActive ? color : '#a8b4c4',
                border: `1px solid ${isActive ? `${color}50` : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              {RELATIONSHIP_LABELS[type] ?? type} ({count})
            </button>
          );
        })}
      </div>

      {/* Concept list */}
      <div className="flex flex-col gap-1">
        {filtered.map((rc, idx) => {
          const color = RELATIONSHIP_COLORS[rc.type] ?? '#888';
          return (
            <button
              key={rc.canonical.id + rc.type + idx}
              onClick={() => onConceptClick(rc.canonical.id)}
              className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors hover:bg-white/5"
              style={{ border: '1px solid rgba(255,255,255,0.04)' }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: color }}
              />
              <span className="text-sm text-how-text-primary font-medium flex-1 truncate">
                {rc.canonical.name}
              </span>
              <span
                className="text-[9px] uppercase tracking-wider font-semibold flex-shrink-0"
                style={{ color }}
              >
                {RELATIONSHIP_LABELS[rc.type] ?? rc.type}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
